import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { onSessionExpired } from '@/auth/sessionExpiredBus';
import { msalInstance } from '@/auth/msalConfig';
import { withBase } from '@/utils/basePath';
import './SessionExpiredBanner.css';

/**
 * Red de seguridad global (bug fila 100, UAT 2026-08-25): se monta UNA vez en `AppRouter` y escucha
 * el evento que dispara `getToken()`/`acquireTokenSilentSafe()` cuando Entra ID exige reautenticación
 * y el popup de recuperación también falla. Cubre cualquier pantalla que no maneje
 * `SessionExpiredError` explícitamente — las 4 de "Traducir auto." ya muestran su propio mensaje más
 * específico y no dependen de este banner, pero tampoco lo ocultan (no hay conflicto: mismo evento,
 * dos avisos independientes).
 *
 * No se autodescarta: a diferencia de un Toast, quedarse visible es intencional.
 *
 * Reporte QA 2026-08-27: un `window.location.reload()` NO arregla nada aquí — MSAL cachea la cuenta
 * en `localStorage` (`msalConfig.ts`), así que sobrevive al reload aunque la sesión de Entra ID ya
 * esté muerta; el siguiente `acquireTokenSilent` automático vuelve a fallar y su intento de
 * recuperación por popup (sin gesto de usuario detrás, viene de una carga de página) lo bloquea el
 * navegador, así que el banner reaparecía de inmediato. `notifySessionExpired()` solo se dispara
 * desde la rama MSAL/AAD de `client.ts`/`acquireToken.ts` (Teams y Frontline resuelven antes por su
 * cuenta), así que este banner nunca aparece en esos dos casos y no hace falta condicionar la
 * llamada.
 *
 * Petición del equipo (2026-08-27/2026-09-11): el botón debe llevar a la pantalla de login PROPIA
 * de la app (`/login`, `LoginView.tsx`) y no saltar directo a Microsoft. Antes llamaba
 * `msalInstance.loginRedirect(loginRequest)` de una vez — funcionalmente resolvía la sesión, pero se
 * saltaba la pantalla propia. `/login` ya tiene su propio botón "Entra con correo Sigma" que dispara
 * ESE MISMO `loginRedirect`, así que una carga completa a `/login` llega al mismo lugar, con la
 * pantalla de la app de por medio.
 *
 * 🔴 `clearCache()` ANTES de navegar (2026-09-13) — sin esto, `/login` no se queda quieto: MSAL
 * sigue teniendo la cuenta cacheada en `localStorage` aunque el TOKEN real ya haya muerto (es la
 * misma cuenta con la que se detectó el banner en primer lugar), y `LoginView` rebota solo de
 * vuelta a `/app` en cuanto ve `getAllAccounts().length > 0` — sin darle a la persona la oportunidad
 * de reautenticarse de verdad con "Entra con correo Sigma". `clearCache()` (nuevo en msal-browser v3,
 * sin navegar a ningún lado, a diferencia de `logoutRedirect`/`logoutPopup`) borra esa cuenta y sus
 * tokens del cliente para que `/login` se quede quieto y muestre el botón real.
 */
export function SessionExpiredBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => onSessionExpired(() => setVisible(true)), []);

  if (!visible) return null;

  return (
    <div className="session-expired-banner" role="alert">
      <i className="fas fa-triangle-exclamation" />
      <span>{t('common.sessionExpiredMessage')}</span>
      <button
        type="button"
        onClick={() => {
          void msalInstance.clearCache().finally(() => {
            window.location.assign(withBase('/login'));
          });
        }}
      >
        {t('common.reload')}
      </button>
    </div>
  );
}
